import { useInfiniteQuery } from 'react-query';
import type { QueryOptions } from 'react-query';
import { searchClient } from 'apis/algolia';

import { FEATURED_SECTIONS } from 'constants/products';
import type { FeaturedSection } from 'constants/products';

import type { IEmployeeWallet } from 'hooks/useQueryProfile';

import {
  getProductsQueryParams,
  groupAndCountResultsForMultipleWallets,
} from 'utils/algolia';
import type { Product } from 'utils/algolia';
import { getUSDConversion } from 'utils/employee';

type CommonArgs = {
  country: string;
  employee_wallets: IEmployeeWallet[];
  exchange_rate: number;
  // featured_section is either a FEATURED_SECTIONS or an
  // account name. We need to refactor this.
  featured_section: FeaturedSection | string;
};

const ALGOLIA_VENDOR_LIST_INDEX = 'vendors_list_v1';

const PRODUCT_LIMIT_PER_PAGE = 9;

const getFeaturedSectionFilters = ({
  employee_wallet,
  exchange_rate,
  featured_section,
}: {
  employee_wallet: IEmployeeWallet;
  exchange_rate: number;
  featured_section: FeaturedSection | string;
}) => {
  switch (featured_section) {
    case FEATURED_SECTIONS.NEW_PRODUCTS: {
      return {
        facet_filters: ['is_new: true'],
        numeric_filters: [],
      };
    }
    case FEATURED_SECTIONS.TWIC_PICKS: {
      return {
        facet_filters: ['is_featured: true'],
        numeric_filters: [],
      };
    }
    case FEATURED_SECTIONS.NO_COST: {
      return {
        facet_filters: [],
        numeric_filters: [
          'num_of_purchase <= 10',
          `usd_msrp <= ${getUSDConversion({
            value: employee_wallet.amount,
            exchange_rate,
          })}`,
        ],
      };
    }
    case FEATURED_SECTIONS.POPULAR: {
      return {
        facet_filters: [],
        numeric_filters: ['num_of_purchase > 10'],
      };
    }
    case FEATURED_SECTIONS.UNPOPULAR: {
      return {
        facet_filters: [],
        numeric_filters: ['num_of_purchase <= 10'],
      };
    }
    default:
      // featured_section is either a FEATURED_SECTIONS or an
      // account name. We need to refactor this.
      return {
        facet_filters: [],
        numeric_filters: [],
      };
  }
};

export const getFeaturedSectionProductsQueryParams = ({
  country,
  employee_wallets,
  exchange_rate,
  featured_section,
}: CommonArgs) =>
  employee_wallets.map((employee_wallet) =>
    getProductsQueryParams({
      country,
      employee_wallet,
      ...getFeaturedSectionFilters({
        employee_wallet,
        exchange_rate,
        featured_section,
      }),
    })
  );

const getFeaturedSectionProducts = async ({
  page,
  ...common_args
}: {
  page: number;
} & CommonArgs) => {
  const featured_section_params =
    getFeaturedSectionProductsQueryParams(common_args);
  const featured_section_queries = featured_section_params.map((params) => ({
    indexName: ALGOLIA_VENDOR_LIST_INDEX,
    query: '',
    params: {
      page,
      hitsPerPage: PRODUCT_LIMIT_PER_PAGE,
      facetFilters: params.facet_filters,
      numericFilters: params.numeric_filters,
    },
  }));
  const { results } = await searchClient.multipleQueries<Product>(
    featured_section_queries
  );
  const { items, pages } = groupAndCountResultsForMultipleWallets(results);
  return {
    products: items,
    max_pages: pages,
  };
};

const getNextPageParam: QueryOptions<
  Awaited<ReturnType<typeof getFeaturedSectionProducts>>
>['getNextPageParam'] = (last_page, pages) =>
  pages.length !== 0 && pages.length < last_page.max_pages
    ? pages.length
    : undefined;

const useInfiniteQueryFeaturedProducts = ({
  onError,
  ...common_args
}: {
  onError: (error: Error) => void;
} & CommonArgs) => {
  const {
    data,
    fetchNextPage,
    hasNextPage: has_next_page,
    isFetchingNextPage: is_fetching_next_page,
    isLoading: is_loading,
  } = useInfiniteQuery(
    `getFeaturedSectionProducts-${common_args.featured_section}`,
    ({ pageParam: page = 0 }) =>
      getFeaturedSectionProducts({
        page,
        ...common_args,
      }),
    {
      getNextPageParam,
      onError,
    }
  );

  return {
    data,
    is_loading,
    is_fetching_next_page,
    fetchNextPage,
    has_next_page,
  };
};

export default useInfiniteQueryFeaturedProducts;
